import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, useContext, useEffect, useState } from 'react';
import BottomDrawer from '@/components/BottomDrawer';
import LikeIcon from '@/components/icons/LikeIcon';
import LoginDrawer from '@/components/LoginDrawer';
import { AuthContext } from '@/context/AuthProvider';
import { ATTACHMENT_TYPE, CommunityPost } from '@/models/Posts';
import likeDislikePost from '@/utils/api/likeDislikePost';
import { getTimeStamp } from '@/utils/helper';
import Caption, { VARIANT as CAPTION_VARIANT } from '../Caption';
import Paragraph, { VARIANT as PARAGRAPH_VARIANT } from '../Paragraph';
import SubHeading, { VARIANT as SUBHEADING_VARAINT } from '../SubHeading';
import LikeShareButtons from './LikeShareButtons';
import MediaCard from './MediaCard';
import UserVerifiedImage from './UserVerifiedImage';
interface CaptionWithIconProps {
  icon: ReactNode;
  caption: string;
}
interface Props extends CommunityPost {
  isShowMore?: boolean;
  index?: number;
  className?: string;
  commentCount: number;
}
const CaptionWithIcon = ({
  icon,
  caption
}: CaptionWithIconProps) => {
  return <div className="flex items-center" data-sentry-component="CaptionWithIcon" data-sentry-source-file="CommunityFeedCard.tsx">
      {icon}
      <Caption variant={CAPTION_VARIANT.ONE} label={caption} className="text-neutral-70" data-sentry-element="Caption" data-sentry-source-file="CommunityFeedCard.tsx" />
    </div>;
};
const CommunityFeedCard = ({
  id,
  attachments,
  content,
  created_at,
  like_count,
  is_liked,
  slug,
  type,
  user,
  tags,
  isShowMore,
  className = '',
  commentCount
}: Props) => {
  const {
    t
  } = useTranslation('main');
  const {
    push
  } = useRouter();
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const [likeCount, setLikeCount] = useState(like_count || 0);
  const [userLiked, setUserLiked] = useState(is_liked || false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  //If user is authenticated the states neeed to be updated as per need
  useEffect(() => {
    if (isAuthenticated) {
      setUserLiked(is_liked);
    }
  }, [isAuthenticated, is_liked]);
  const toggleLikeBtn = async () => {
    if (!isAuthenticated) {
      toggleDrawer();
      return;
    }
    setLoading(true);
    const result = await likeDislikePost(id, 'community');
    if (result) {
      if (userLiked) {
        setLikeCount(prev => prev - 1);
      } else {
        setLikeCount(prev => prev + 1);
      }
      setUserLiked(prev => !prev);
    } else {
      //TODO :: handle error
    }
    setLoading(false);
  };
  const toggleDrawer = () => {
    setIsDrawerOpen(prev => !prev);
  };
  //If user on home page clicking on media can route user to feed specific page
  // If user on feed specific page nothing will hapen on clicking media
  const onMediaClick = () => {
    if (isShowMore) {
      push(`/community/post/${slug}`);
    }
  };
  return <section className={`w-full max-w-4xl ${className}`} data-sentry-component="CommunityFeedCard" data-sentry-source-file="CommunityFeedCard.tsx">
      <div className="flex items-center">
        <UserVerifiedImage alt={user.full_name} imageUrl={user.photo_S3_path} isVerified={user.is_verified} data-sentry-element="UserVerifiedImage" data-sentry-source-file="CommunityFeedCard.tsx" />
        <div className="ml-2.5">
          <SubHeading variant={SUBHEADING_VARAINT.BIG} data-sentry-element="SubHeading" data-sentry-source-file="CommunityFeedCard.tsx">
            {user.full_name}
          </SubHeading>

          {user.profile ? <Paragraph variant={PARAGRAPH_VARIANT.MEDIUM} className="text-neutral-70 mt-1">
              {user.profile.district !== null ? user.profile.district : ''}
              {user.profile.state !== null ? `, ${user.profile.state}` : ''}
            </Paragraph> : null}
        </div>
      </div>
      {isShowMore && <Paragraph variant={PARAGRAPH_VARIANT.BIG} className="line-clamp-3 my-2">
          {content.text}
        </Paragraph>}
      {isShowMore ? <div className="flex justify-end">
          <Link href={`/community/post/${slug}`}>
            <span className="text-primary-100 cursor-pointer text-sm">
              {t('read_more_btn')}
            </span>
          </Link>
        </div> : null}
      {type !== ATTACHMENT_TYPE.Text && <div className={`my-2 flex items-center justify-center cursor-pointer ${type !== ATTACHMENT_TYPE.Audio ? 'h-[300px] md:h-[600px]' : ''}`} data-testid="media-card" onClick={onMediaClick}>
          <MediaCard attachments={attachments} slug={slug} />
        </div>}
      {!isShowMore && <Paragraph variant={PARAGRAPH_VARIANT.BIG} className="my-2">
          {content.text}
        </Paragraph>}
      <div className="flex justify-between my-4">
        <div className="flex">
          {likeCount > 0 && <CaptionWithIcon icon={<LikeIcon className="h-4 w-4 stroke-[20px] stroke-primary-100 fill-primary-100 mr-1" />} caption={`${likeCount} ${likeCount > 1 ? t('total_likes_caption') : t('total_like_caption')}`} />}
          {commentCount > 0 && <CaptionWithIcon icon={likeCount > 0 ? <div className="w-2 h-2 bg-neutral-60 rounded-full mx-4"></div> : null} caption={`${commentCount} ${commentCount > 1 ? t('total_comments_caption') : t('total_comment_caption')}`} />}
        </div>
        <CaptionWithIcon icon={null} caption={getTimeStamp(created_at)} data-sentry-element="CaptionWithIcon" data-sentry-source-file="CommunityFeedCard.tsx" />
      </div>
      <LikeShareButtons liked={userLiked} onLikeClick={toggleLikeBtn} slug={slug} loading={loading} postType="community" tags={tags} data-sentry-element="LikeShareButtons" data-sentry-source-file="CommunityFeedCard.tsx" />
      <BottomDrawer show={isDrawerOpen} onClose={toggleDrawer} data-sentry-element="BottomDrawer" data-sentry-source-file="CommunityFeedCard.tsx">
        <LoginDrawer data-sentry-element="LoginDrawer" data-sentry-source-file="CommunityFeedCard.tsx" />
      </BottomDrawer>
    </section>;
};
export default CommunityFeedCard;